/**
 * UCUM unit registry. CLAUDE.md §8.2 (UCUM codes every quantity) and §16.1
 * (every quantity crossing a boundary carries an explicit UCUM unit).
 *
 * Two ideas keep this safe, and they are separate on purpose:
 *
 *  1. A UNIT has a physical DIMENSION. Conversion inside one dimension is a pure
 *     scale factor and needs nothing else. `kg` → `[lb_av]` is always the same.
 *
 *  2. An ANALYTE is what is being measured. Converting between a mass
 *     concentration (mg/dL) and a substance concentration (mmol/L) requires the
 *     analyte's molar mass — glucose and creatinine are both reported in mg/dL
 *     and convert to mmol/L by different factors.
 *
 * Collapsing those two ideas is the classic way to divide a creatinine by the
 * glucose factor and produce a plausible, wrong number. So the unit registry
 * below carries no analyte knowledge, and any conversion that needs an analyte
 * must name it (see convert.ts).
 *
 * The registry is a closed set rather than a general UCUM parser: adding a unit
 * should require a deliberate review with a cited conversion factor.
 */
export const UCUM_CODES = [
    'mg/dL',
    'mg/L',
    'g/L',
    'mmol/L',
    'umol/L',
    'mEq/L',
    'kg',
    'g',
    'mg',
    '[lb_av]',
    'cm',
    'm',
    '[in_i]',
    'mg/g',
    'mg/mmol',
    'mL/min/{1.73_m2}',
    'a',
    'd',
    'h',
    'min',
    'U/L',
    '10*9/L',
    '10*3/uL',
    'mm[Hg]',
    'mOsm/kg',
    '[iU]',
    '%',
    'mmol/mol',
    'kg/m2',
    '1',
];
/**
 * Canonical unit per dimension. §16.1: store canonical internally, convert at
 * the presentation edge. Glucose is therefore stored mg/dL.
 */
export const CANONICAL_UNIT = {
    'mass-concentration': 'mg/dL',
    'substance-concentration': 'mmol/L',
    mass: 'kg',
    length: 'cm',
    'mass-per-mass': 'mg/g',
    'mass-per-substance': 'mg/mmol',
    egfr: 'mL/min/{1.73_m2}',
    time: 'a',
    'catalytic-activity': 'U/L',
    'count-concentration': '10*9/L',
    pressure: 'mm[Hg]',
    osmolality: 'mOsm/kg',
    'insulin-dose': '[iU]',
    fraction: '%',
    'hba1c-ifcc': 'mmol/mol',
    'body-mass-index': 'kg/m2',
    dimensionless: '1',
};
export const UNITS = {
    // -- mass concentration (canonical mg/dL) ---------------------------------
    'mg/dL': {
        dimension: 'mass-concentration',
        toCanonical: 1,
        display: 'mg/dL',
        source: 'canonical (§16.1)',
    },
    'mg/L': {
        dimension: 'mass-concentration',
        toCanonical: 0.1,
        display: 'mg/L',
        source: 'SI: 1 mg/L = 0.1 mg/dL',
    },
    'g/L': {
        dimension: 'mass-concentration',
        toCanonical: 100,
        display: 'g/L',
        source: 'SI: 1 g/L = 100 mg/dL',
    },
    // -- substance concentration (canonical mmol/L) ---------------------------
    'mmol/L': {
        dimension: 'substance-concentration',
        toCanonical: 1,
        display: 'mmol/L',
        source: 'canonical',
    },
    'umol/L': {
        dimension: 'substance-concentration',
        toCanonical: 0.001,
        display: 'µmol/L',
        source: 'SI',
    },
    'mEq/L': {
        dimension: 'substance-concentration',
        toCanonical: 1,
        display: 'mEq/L',
        // Only valid for monovalent ions (Na+, K+, Cl-, HCO3-), which is the only
        // use in this platform. A divalent ion would need charge, not just moles.
        source: 'mEq/L = mmol/L for monovalent ions only',
    },
    // -- mass -----------------------------------------------------------------
    kg: { dimension: 'mass', toCanonical: 1, display: 'kg', source: 'canonical' },
    g: { dimension: 'mass', toCanonical: 0.001, display: 'g', source: 'SI' },
    // Medication doses are written in mg. It was missing here, and because
    // nothing validated a Quantity's unit on write, an unregistered unit
    // reached storage unremarked -- see services/fhir-gateway code-validation.
    mg: { dimension: 'mass', toCanonical: 0.000001, display: 'mg', source: 'SI' },
    '[lb_av]': {
        dimension: 'mass',
        toCanonical: 0.45359237,
        display: 'lb',
        source: 'NIST HB 44, exact',
    },
    // -- length ---------------------------------------------------------------
    cm: { dimension: 'length', toCanonical: 1, display: 'cm', source: 'canonical' },
    m: { dimension: 'length', toCanonical: 100, display: 'm', source: 'SI' },
    '[in_i]': { dimension: 'length', toCanonical: 2.54, display: 'in', source: 'NIST HB 44, exact' },
    // -- albumin-to-creatinine ratio ------------------------------------------
    // mg/g and mg/mmol are different dimensions (mass-per-mass vs
    // mass-per-substance). Converting between them needs creatinine's molar mass,
    // so it lives in convert.ts as an analyte-aware conversion, not a factor here.
    'mg/g': {
        dimension: 'mass-per-mass',
        toCanonical: 1,
        display: 'mg/g',
        source: 'canonical (§2.4 UACR)',
    },
    'mg/mmol': {
        dimension: 'mass-per-substance',
        toCanonical: 1,
        display: 'mg/mmol',
        source: 'canonical',
    },
    // -- eGFR -----------------------------------------------------------------
    'mL/min/{1.73_m2}': {
        dimension: 'egfr',
        toCanonical: 1,
        display: 'mL/min/1.73m²',
        source: 'canonical',
    },
    // -- time -----------------------------------------------------------------
    // §16.2 forbids computing calendar days by dividing seconds. These factors are
    // for durations only (e.g. an age in years for a risk equation), never for
    // deriving a calendar date.
    a: { dimension: 'time', toCanonical: 1, display: 'years', source: 'canonical' },
    d: {
        dimension: 'time',
        toCanonical: 1 / 365.25,
        display: 'days',
        source: 'Julian year, duration only',
    },
    h: {
        dimension: 'time',
        toCanonical: 1 / (365.25 * 24),
        display: 'hours',
        source: 'Julian year, duration only',
    },
    min: {
        dimension: 'time',
        toCanonical: 1 / (365.25 * 24 * 60),
        display: 'minutes',
        source: 'Julian year, duration only',
    },
    // -- enzymes, counts, pressures -------------------------------------------
    'U/L': { dimension: 'catalytic-activity', toCanonical: 1, display: 'U/L', source: 'canonical' },
    '10*9/L': {
        dimension: 'count-concentration',
        toCanonical: 1,
        display: '×10⁹/L',
        source: 'canonical',
    },
    '10*3/uL': {
        dimension: 'count-concentration',
        toCanonical: 1,
        display: '×10³/µL',
        source: '10³/µL is numerically identical to 10⁹/L',
    },
    'mm[Hg]': { dimension: 'pressure', toCanonical: 1, display: 'mmHg', source: 'canonical' },
    'mOsm/kg': { dimension: 'osmolality', toCanonical: 1, display: 'mOsm/kg', source: 'canonical' },
    '[iU]': { dimension: 'insulin-dose', toCanonical: 1, display: 'units', source: 'canonical' },
    // -- fractions and HbA1c --------------------------------------------------
    // NGSP % is a plain percentage. IFCC mmol/mol is its own dimension because the
    // NGSP<->IFCC relation is affine, not a scale factor: it cannot be a
    // `toCanonical` multiplier. See convertHba1c() in convert.ts.
    '%': { dimension: 'fraction', toCanonical: 1, display: '%', source: 'canonical' },
    'mmol/mol': {
        dimension: 'hba1c-ifcc',
        toCanonical: 1,
        display: 'mmol/mol',
        source: 'IFCC; affine to NGSP — see convertHba1c()',
    },
    // Its own dimension on purpose. BMI converts to nothing else, and giving it a
    // dimension of its own is what makes "arithmetic between incompatible units is
    // an error" (16.1) hold for it rather than letting it be mistaken for a plain
    // ratio.
    'kg/m2': {
        dimension: 'body-mass-index',
        toCanonical: 1,
        display: 'kg/m²',
        source: 'canonical (§2.4 BMI)',
    },
    '1': { dimension: 'dimensionless', toCanonical: 1, display: '', source: 'canonical' },
};
export const ANALYTES = {
    glucose: {
        id: 'glucose',
        // CLAUDE.md §2.4 pins this exactly: "mmol/L = mg/dL / 18.0182".
        mgPerDlPerMmolPerL: 18.0182,
        molarMassGPerMol: 180.156,
        source: 'CLAUDE.md §2.4 (factor is contractual and must not be re-derived)',
    },
    creatinine: {
        id: 'creatinine',
        // Molar mass 113.12 g/mol, so 1 mmol/L = 113.12 mg/L = 11.312 mg/dL,
        // equivalently 1 mg/dL = 88.4017 µmol/L.
        mgPerDlPerMmolPerL: 11.312,
        molarMassGPerMol: 113.12,
        source: 'Creatinine molar mass 113.12 g/mol; 1 mg/dL = 88.4017 µmol/L',
    },
};
/** Direct UACR factor: 1 mg/mmol creatinine = 8.8402 mg/g creatinine. */
export const UACR_MG_PER_G_PER_MG_PER_MMOL = 8.8402;
export const isUcumCode = (value) => Object.prototype.hasOwnProperty.call(UNITS, value);
export const dimensionOf = (unit) => UNITS[unit].dimension;
export const displayOf = (unit) => UNITS[unit].display;
export const sourceOf = (unit) => UNITS[unit].source;
//# sourceMappingURL=ucum.js.map